<script lang="ts">
	import { Dropdown, DropdownDivider, DropdownGroup, DropdownHeader, DropdownItem, Heading, type TriggeredToggleEvent } from "flowbite-svelte-v1";
	import { Search } from "flowbite-svelte-v1";
	import DataTypeBadge from "#lib/components/DataTypeBadge.svelte";
	import Icon from "#lib/components/Icon.svelte";
	import P from "#lib/components/P.svelte";
	import { autofocus } from "#lib/directives/autofocus.js";
	import { providerIcons } from "#lib/iconProvider.js";
	import type { SubstitutionToken, TokenProvider } from "#lib/tokenProvider.js";

	interface Props {
		open: boolean;
		provider: TokenProvider;
		onselect: (token: SubstitutionToken) => void;
		onclose?: () => void;
	}

	let { open = $bindable(), provider, onselect, onclose }: Props = $props();

	let searchTerm: string | undefined = $state(undefined);
	let selection = $state(-1);

	let tokensByCategory = $derived(
		(searchTerm ? provider.suggest(searchTerm).map(suggestion => suggestion.value) : provider.tokens()).reduce(
			(acc, token) => {
				if (!token) {
					return acc;
				}
				const key = token.providerName ?? token.provider;
				if (!acc[key]) {
					acc[key] = [];
				}
				acc[key].push(token);
				return acc;
			},
			{} as Record<string, SubstitutionToken[]>,
		),
	);

	let tokens = $derived(
		Object.keys(tokensByCategory)
			.toSorted()
			.reduce((acc, key) => {
				acc.push(...tokensByCategory[key].toSorted((a, b) => (a.label < b.label ? -1 : 1)));
				return acc;
			}, [] as SubstitutionToken[]),
	);

	function nextSelection() {
		if (selection + 1 < tokens.length) {
			selection += 1;
		}
	}
	function prevSelection() {
		if (selection > 0) {
			selection -= 1;
		}
	}

	function handleClose(e: TriggeredToggleEvent) {
		if (e.newState === "closed") {
			reset();
		}
	}

	function handleSelect(token: SubstitutionToken) {
		onselect(token);
		reset();
	}

	async function keyDown(event: KeyboardEvent) {
		switch (event.code) {
			case "Escape":
				event.preventDefault();
				event.stopImmediatePropagation();
				reset();
				break;
			case "ArrowUp":
				event.preventDefault();
				event.stopImmediatePropagation();
				prevSelection();
				scrollIntoView(selection);
				break;
			case "ArrowDown":
				event.preventDefault();
				event.stopImmediatePropagation();
				nextSelection();
				scrollIntoView(selection);
				break;
			case "Enter":
				event.preventDefault();
				event.stopImmediatePropagation();
				if (selection < 0 || selection >= tokens.length) {
					break;
				}
				handleSelect(tokens[selection]);
				break;
			default:
				selection = 0;
		}
	}

	function scrollIntoView(index: number) {
		const element = document.getElementById(`token-menu-item-${index}`);
		if (element) {
			element.scrollIntoView({ block: "nearest", behavior: "smooth" });
		}
	}

	function reset() {
		searchTerm = "";
		selection = -1;
		open = false;
		onclose?.();
	}
</script>

<Dropdown bind:isOpen={open} class="z-50 mt-2 divide-y divide-gray-200 border border-gray-300" triggerDelay={0} transitionParams={{ duration: 0 }} ontoggle={handleClose} onkeydown={keyDown}>
	<DropdownHeader class="flex flex-col gap-4">
		<Heading tag="h2" class="text-base text-gray-900">Add property</Heading>
		<div use:autofocus>
			<Search size="md" placeholder="Search for a property" bind:value={searchTerm} onkeydown={keyDown} clearable classes={{ svg: "text-primary-600" }} />
		</div>
	</DropdownHeader>
	<DropdownGroup>
		<div class="h-full max-h-75 min-h-0 w-150 flex-none scroll-p-10 overflow-y-auto px-0">
			{#each Object.keys(tokensByCategory).toSorted() as category, index (category)}
				{@const icon = providerIcons.get(tokensByCategory[category][0]?.provider)}
				{#if index > 0}
					<DropdownDivider class="bg-gray-200" />
				{/if}
				<div class="sticky top-0 flex w-full items-center gap-2 bg-white px-4 py-2">
					<Icon iconSet={icon?.iconSet} name={icon?.icon} class="h-5 w-5 flex-none text-gray-500" />
					<Heading tag="h3" class="text-xs font-normal text-gray-500 uppercase">{category}</Heading>
				</div>
				{#each tokensByCategory[category].toSorted((a, b) => (a.label < b.label ? -1 : 1)) as token (token.value)}
					{@const tokenIndex = tokens.indexOf(token)}
					<DropdownItem id={`token-menu-item-${tokenIndex}`} class={tokenIndex === selection ? "bg-gray-100 px-4 py-3 dark:bg-gray-600" : "px-4 py-3"} onclick={() => handleSelect(token)} onmouseenter={() => (selection = tokenIndex)} onfocus={() => (selection = tokenIndex)}>
						<div class="flex w-full items-start gap-3">
							<div class="flex grow flex-col">
								<Heading tag="h4" class="text-sm font-medium text-gray-900">{token.label}</Heading>
								{#if token.description}
									<P class="line-clamp-2" size="sm" secondary>{token.description}</P>
								{/if}
							</div>
							<DataTypeBadge dataType={token.dataType} />
						</div>
					</DropdownItem>
				{/each}
			{:else}
				<P align="center" size="sm" color="text-gray-500" secondary class="mt-8 mb-1">{searchTerm?.length ? "No properties found for search term." : "No compatible data properties are set up for this flow."}<br /></P>
				<P align="center" color="text-gray-400" size="sm" secondary class="mb-8">Visit the codes section to add properties.</P>
			{/each}
		</div>
	</DropdownGroup>
</Dropdown>
